import { useEffect, useReducer, useState } from "react";
import "./App.css";
import {
  COLS,
  ROWS,
  SHAPES,
  Kind,
  ghostY,
  initialState,
  levelInterval,
  reducer,
} from "./game";
import { isMuted, play, setMuted } from "./sound";

const S = 30; // cell size in SVG units

const COLORS: Record<Kind, string> = {
  I: "#3ec6e0",
  O: "#f2c14e",
  T: "#a57be0",
  S: "#7dc46b",
  Z: "#ef5b5b",
  J: "#4f83d9",
  L: "#f28c38",
};

function Block({
  x,
  y,
  kind,
  ghost = false,
  size = S,
}: {
  x: number;
  y: number;
  kind: Kind;
  ghost?: boolean;
  size?: number;
}) {
  const color = COLORS[kind];
  return (
    <g transform={`translate(${x * size},${y * size})`}>
      <rect
        x={1}
        y={1}
        width={size - 2}
        height={size - 2}
        rx={4}
        fill={ghost ? "none" : color}
        stroke={ghost ? color : "rgba(255,255,255,.22)"}
        strokeWidth={ghost ? 2 : 1}
        opacity={ghost ? 0.45 : 1}
      />
      {!ghost && (
        <rect
          x={3}
          y={3}
          width={size - 6}
          height={(size - 6) / 3}
          rx={2}
          fill="#fff"
          opacity={0.2}
        />
      )}
    </g>
  );
}

export default function App() {
  const [s, dispatch] = useReducer(reducer, undefined, initialState);
  const [muted, setMutedState] = useState(false);

  // Gravity
  useEffect(() => {
    if (s.status !== "playing") return;
    const id = setInterval(
      () => dispatch({ type: "tick" }),
      levelInterval(s.level),
    );
    return () => clearInterval(id);
  }, [s.status, s.level]);

  // Sounds
  useEffect(() => {
    if (s.event) play(s.event);
  }, [s.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, () => void> = {
        ArrowLeft: () => dispatch({ type: "left" }),
        ArrowRight: () => dispatch({ type: "right" }),
        ArrowDown: () => dispatch({ type: "down" }),
        ArrowUp: () => dispatch({ type: "rotate" }),
        x: () => dispatch({ type: "rotate" }),
        " ": () => dispatch({ type: "hard" }),
        p: () => dispatch({ type: "pause" }),
        Enter: () => {
          if (s.status === "ready" || s.status === "over")
            dispatch({ type: "start" });
        },
        m: toggleMute,
      };
      const fn = map[e.key];
      if (fn) {
        e.preventDefault();
        fn();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [s.status]);

  function toggleMute() {
    setMuted(!isMuted());
    setMutedState(isMuted());
  }

  const gy = ghostY(s.board, s.piece);
  const overlay =
    s.status === "ready"
      ? ["Press Enter to play", ""]
      : s.status === "paused"
        ? ["Paused", "Press P to resume"]
        : s.status === "over"
          ? ["Game over", "Press Enter to play again"]
          : null;

  return (
    <main className="game">
      <svg
        className="board"
        viewBox={`0 0 ${COLS * S} ${ROWS * S}`}
        role="img"
        aria-label="Tetris board"
      >
        <rect width={COLS * S} height={ROWS * S} fill="#1a1d2a" />
        {Array.from({ length: COLS - 1 }, (_, i) => (
          <line
            key={`v${i}`}
            x1={(i + 1) * S}
            x2={(i + 1) * S}
            y1={0}
            y2={ROWS * S}
            stroke="#252939"
          />
        ))}
        {Array.from({ length: ROWS - 1 }, (_, i) => (
          <line
            key={`h${i}`}
            y1={(i + 1) * S}
            y2={(i + 1) * S}
            x1={0}
            x2={COLS * S}
            stroke="#252939"
          />
        ))}
        {s.board.map((row, y) =>
          row.map(
            (k, x) => k && <Block key={`${x}-${y}`} x={x} y={y} kind={k} />,
          ),
        )}
        {s.status !== "ready" &&
          s.status !== "over" &&
          s.piece.m.map((row, r) =>
            row.map((v, c) =>
              v ? (
                <Block
                  key={`g${r}${c}`}
                  x={s.piece.x + c}
                  y={gy + r}
                  kind={s.piece.kind}
                  ghost
                />
              ) : null,
            ),
          )}
        {s.status !== "ready" &&
          s.piece.m.map((row, r) =>
            row.map((v, c) =>
              v && s.piece.y + r >= 0 ? (
                <Block
                  key={`p${r}${c}`}
                  x={s.piece.x + c}
                  y={s.piece.y + r}
                  kind={s.piece.kind}
                />
              ) : null,
            ),
          )}
        {overlay && (
          <g>
            <rect
              width={COLS * S}
              height={ROWS * S}
              fill="#0d0f16"
              opacity={0.78}
            />
            <text
              x={(COLS * S) / 2}
              y={(ROWS * S) / 2 - 8}
              textAnchor="middle"
              className="ov-title"
            >
              {overlay[0]}
            </text>
            <text
              x={(COLS * S) / 2}
              y={(ROWS * S) / 2 + 20}
              textAnchor="middle"
              className="ov-sub"
            >
              {overlay[1]}
            </text>
          </g>
        )}
      </svg>

      <aside className="panel">
        <h1>Tetris</h1>
        <dl className="stats">
          <div>
            <dt>Score</dt>
            <dd>{s.score}</dd>
          </div>
          <div>
            <dt>Level</dt>
            <dd>{s.level}</dd>
          </div>
          <div>
            <dt>Lines</dt>
            <dd>{s.lines}</dd>
          </div>
        </dl>
        <div>
          <p className="label">Next</p>
          <svg viewBox="0 0 120 90" className="next" aria-label="Next piece">
            {(() => {
              const m = SHAPES[s.next],
                size = 24;
              const ox = (120 - m[0].length * size) / 2 / size;
              const oy = (90 - m.length * size) / 2 / size;
              return m.map((row, r) =>
                row.map((v, c) =>
                  v ? (
                    <Block
                      key={`${r}${c}`}
                      x={ox + c}
                      y={oy + r}
                      kind={s.next}
                      size={size}
                    />
                  ) : null,
                ),
              );
            })()}
          </svg>
        </div>
        <ul className="keys">
          <li>
            <kbd>← →</kbd> Move
          </li>
          <li>
            <kbd>↑</kbd> / <kbd>X</kbd> Rotate
          </li>
          <li>
            <kbd>↓</kbd> Soft drop
          </li>
          <li>
            <kbd>Space</kbd> Hard drop
          </li>
          <li>
            <kbd>P</kbd> Pause
          </li>
        </ul>
        <button onClick={toggleMute}>
          {muted ? "Sound off (M)" : "Sound on (M)"}
        </button>
      </aside>
    </main>
  );
}
